"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  Cable,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Layers3,
  MapPinned,
  Save,
  Undo2,
  Wind,
  Wrench,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import type { DeviceChannelCreateDto, DeviceChannelDto, DeviceDto } from "@/lib/api/types";
import type { JsonObject, JsonValue } from "@/lib/api/types/json";
import { Button } from "@/components/ds/button";
import { PageHeader } from "@/components/ds/workspace-page";
import { StatusIndicator } from "@/components/ui/status-indicator";
import { Card } from "@/components/ui-system";
import { cn } from "@/lib/utils/cn";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import {
  deviceActivityModelFromApi,
  DeviceOperationalStatus,
  formatDeviceOperationalStatusLabel,
  operationalStatusToIndicatorVariant,
} from "@/lib/devices/device-state-model";

import {
  AlertsTab,
  DataSamplingTab,
  INVALID_FIELD_ATTR,
  invalidFieldKeyForError,
  LifecycleTab,
  QrPublicTab,
  VisualizationsTab,
} from "@/modules/core/datalogger-create/components/wizard";
import { StepBasic } from "@/modules/core/datalogger-create/components/steps/step-basic";
import { StepBasicConnectivity } from "@/modules/core/datalogger-create/components/steps/step-basic-connectivity";
import { StepChannels } from "@/modules/core/datalogger-create/components/steps/step-channels";
import { StepTabs } from "@/modules/core/datalogger-create/components/step-tabs";
import { useDataloggerWizardStore } from "@/modules/core/datalogger-create/hooks/use-datalogger-form";
import {
  defaultGraphTypeForDataType,
  createDefaultChannel,
  samplesPerPeriodToHz,
  sanitizeAggregationsForDataType,
  sanitizeGraphTypeForDataType,
  snapshotToMetadata,
  type ChannelAggregation,
  type ChannelFormulaOperator,
  type ChannelRow,
  type DataloggerAlertCondition,
  type DataloggerAlertRecipients,
  type DataloggerFormSnapshot,
  type DataloggerLifecycle,
  type DataloggerVisualizations,
  type HardwareProtocolSettings,
  type SamplingRatePeriod,
  type TransmissionMode,
  validateAlertTemplates,
  validateBasic,
  validateChannels,
  validateHardware,
  validateVisualizations,
} from "@/modules/core/datalogger-create/schema/datalogger.schema";
import {
  buildBluetoothSerialConnectionPayload,
  buildMqttConnectionPayload,
  buildWebsocketConnectionPayload,
} from "@/modules/core/datalogger-create/utils/build-create-payload";
import {
  useCreateDeviceChannelMutation,
  useDeleteDeviceChannelMutation,
  usePatchDeviceMutation,
  useUpdateDeviceChannelMutation,
} from "@/modules/devices/hooks";
import { DeviceDebugAudit } from "@/modules/devices/components/device-debug-audit";
import { DeviceServiceRecords } from "@/modules/devices/components/device-service-records";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "channels", label: "Channels" },
  { id: "visualizations", label: "Visualizations" },
  { id: "data", label: "Data & sampling" },
  { id: "alerts", label: "Alerts" },
  { id: "connectivity", label: "Connectivity" },
  { id: "lifecycle", label: "Lifecycle" },
  { id: "qr", label: "QR / public" },
  { id: "debug", label: "Debug / logs" },
] as const;
const TAB_ICONS = [MapPinned, Layers3, Wind, Gauge, Bell, Cable, Wrench, CheckCircle2, Wrench] as const;

const CONTROLLED_METADATA_KEYS = [
  "schema_version",
  "kind",
  "basic",
  "hardware",
  "alerts",
  "channels",
  "visualizations",
  "installation_date",
  "warranty_end",
  "next_calibration",
  "latitude",
  "longitude",
  "lat",
  "lng",
  "location",
  "city",
  "state_id",
  "state",
  "tags",
  "description",
  "sim_type",
  "phone_number",
  "imei_number",
  "device_photo_path",
  "device_photo_data_url",
  "transmission_mode",
  "protocol",
  "mqttBroker",
  "mqttPort",
  "mqttTls",
  "mqttTopicPrefix",
  "mqttTopic",
  "mqttUsername",
  "mqttPassword",
  "mqttPublisherClientId",
  "httpBaseUrl",
  "websocketUrl",
  "pollIntervalSeconds",
  "bluetoothDeviceName",
  "bluetoothPort",
  "bluetoothBaudRate",
  "bluetoothDataFormat",
  "bluetoothLineEnding",
  "bluetoothNote",
  "lanServerHost",
  "lanServerPort",
  "lanServerUseTls",
  "ethernetDhcp",
  "ethernetIp",
  "ethernetGateway",
  "ethernetNetmask",
  "ethernetDns",
  "wifiSsid",
  "wifiPassword",
  "wifiSecurityType",
  "wifiUseDhcp",
  "wifiStaticIp",
  "wifiGateway",
  "wifiNetmask",
  "wifiDns",
  "cellularApn",
  "modbusHost",
  "modbusPort",
  "modbusUnitId",
  "loraAppEui",
  "loraDevEui",
  "mqtt_broker",
  "mqtt_port",
  "site",
] as const;

const ALLOWED_CHANNEL_AGGREGATIONS = new Set<ChannelAggregation>(["avg", "min", "max"]);

export type DeviceDetailTabId = (typeof TABS)[number]["id"];

function isTabId(v: string | null | undefined): v is DeviceDetailTabId {
  return v != null && TABS.some((t) => t.id === v);
}

function resolveTab(raw: string | null | undefined): DeviceDetailTabId {
  if (raw === "location") {
    return "overview";
  }
  return isTabId(raw) ? raw : "overview";
}

function asJsonObject(value: unknown): JsonObject {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {};
}

function readString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function readNullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function readAlertAudienceUserIds(value: unknown): string[] {
  return readStringArray(value);
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function readSamplingPeriod(value: unknown): SamplingRatePeriod {
  return value === "second" || value === "minute" || value === "hour" || value === "day" ? value : "second";
}

function samplingPeriodMeta(period: SamplingRatePeriod): string {
  if (period === "minute") return "Per minute";
  if (period === "hour") return "Per hour";
  if (period === "day") return "Per day";
  return "Per second";
}

function readTransmissionMode(value: unknown): TransmissionMode {
  return value === "mqtt" ||
    value === "http" ||
    value === "websocket" ||
    value === "polling" ||
    value === "ethernet" ||
    value === "wifi" ||
    value === "cellular" ||
    value === "bluetooth_serial" ||
    value === "modbus_tcp" ||
    value === "lora"
    ? value
    : value === "bluetooth" || value === "bluetooth_le"
      ? "bluetooth_serial"
      : "mqtt";
}

function readChannelFormulaOperator(value: unknown): ChannelFormulaOperator {
  return value === "add" || value === "subtract" || value === "multiply" || value === "divide" ? value : "add";
}

function mapChannelDataType(
  apiDataType: DeviceChannelDto["data_type"] | null | undefined,
  metadataDataType: unknown,
): ChannelRow["dataType"] {
  if (
    metadataDataType === "float" ||
    metadataDataType === "int" ||
    metadataDataType === "bool" ||
    metadataDataType === "string" ||
    metadataDataType === "formula"
  ) {
    return metadataDataType;
  }
  if (apiDataType === "boolean") {
    return "bool";
  }
  if (apiDataType === "string") {
    return "string";
  }
  return "float";
}

function mapChannelAggregations(value: unknown): ChannelAggregation[] {
  if (!Array.isArray(value)) {
    return ["avg"];
  }
  const parsed = value.filter(
    (item): item is ChannelAggregation =>
      item === "avg" || item === "min" || item === "max" || item === "sum" || item === "last" || item === "count",
  );
  return parsed.length > 0 ? parsed : ["avg"];
}

function extractProtocolSettings(meta: JsonObject, hardwareMeta: JsonObject): HardwareProtocolSettings {
  const protocolMeta = asJsonObject(hardwareMeta.protocolSettings);
  return {
    mqttBroker: readNullableString(protocolMeta.mqttBroker ?? meta.mqttBroker ?? meta.mqtt_broker) ?? undefined,
    mqttPort: readNullableString(protocolMeta.mqttPort ?? meta.mqttPort ?? meta.mqtt_port) ?? undefined,
    mqttTls: typeof (protocolMeta.mqttTls ?? meta.mqttTls) === "boolean" ? Boolean(protocolMeta.mqttTls ?? meta.mqttTls) : undefined,
    mqttTopicPrefix: readNullableString(protocolMeta.mqttTopicPrefix ?? meta.mqttTopicPrefix) ?? undefined,
    mqttTopic: readNullableString(protocolMeta.mqttTopic ?? meta.mqttTopic) ?? undefined,
    mqttUsername: readNullableString(protocolMeta.mqttUsername ?? meta.mqttUsername) ?? undefined,
    mqttPublisherClientId:
      readNullableString(protocolMeta.mqttPublisherClientId ?? meta.mqttPublisherClientId) ?? undefined,
    websocketUrl: readNullableString(protocolMeta.websocketUrl ?? meta.websocketUrl) ?? undefined,
    pollIntervalSeconds: readNullableString(protocolMeta.pollIntervalSeconds ?? meta.pollIntervalSeconds) ?? undefined,
    bluetoothDeviceName: readNullableString(protocolMeta.bluetoothDeviceName ?? meta.bluetoothDeviceName) ?? undefined,
    bluetoothPort: readNullableString(protocolMeta.bluetoothPort ?? meta.bluetoothPort) ?? undefined,
    bluetoothBaudRate: readNullableString(protocolMeta.bluetoothBaudRate ?? meta.bluetoothBaudRate) ?? undefined,
    bluetoothDataFormat: readNullableString(protocolMeta.bluetoothDataFormat ?? meta.bluetoothDataFormat) ?? undefined,
    bluetoothLineEnding: readNullableString(protocolMeta.bluetoothLineEnding ?? meta.bluetoothLineEnding) ?? undefined,
    bluetoothNote: readNullableString(protocolMeta.bluetoothNote ?? meta.bluetoothNote) ?? undefined,
    lanServerHost: readNullableString(protocolMeta.lanServerHost ?? meta.lanServerHost) ?? undefined,
    lanServerPort: readNullableString(protocolMeta.lanServerPort ?? meta.lanServerPort) ?? undefined,
    lanServerUseTls:
      typeof (protocolMeta.lanServerUseTls ?? meta.lanServerUseTls) === "boolean"
        ? Boolean(protocolMeta.lanServerUseTls ?? meta.lanServerUseTls)
        : undefined,
    ethernetDhcp: typeof (protocolMeta.ethernetDhcp ?? meta.ethernetDhcp) === "boolean" ? Boolean(protocolMeta.ethernetDhcp ?? meta.ethernetDhcp) : undefined,
    ethernetIp: readNullableString(protocolMeta.ethernetIp ?? meta.ethernetIp) ?? undefined,
    ethernetGateway: readNullableString(protocolMeta.ethernetGateway ?? meta.ethernetGateway) ?? undefined,
    ethernetNetmask: readNullableString(protocolMeta.ethernetNetmask ?? meta.ethernetNetmask) ?? undefined,
    ethernetDns: readNullableString(protocolMeta.ethernetDns ?? meta.ethernetDns) ?? undefined,
    wifiSsid: readNullableString(protocolMeta.wifiSsid ?? meta.wifiSsid) ?? undefined,
    wifiPassword: readNullableString(protocolMeta.wifiPassword ?? meta.wifiPassword) ?? undefined,
    wifiSecurityType: readNullableString(protocolMeta.wifiSecurityType ?? meta.wifiSecurityType) ?? undefined,
    wifiUseDhcp: typeof (protocolMeta.wifiUseDhcp ?? meta.wifiUseDhcp) === "boolean" ? Boolean(protocolMeta.wifiUseDhcp ?? meta.wifiUseDhcp) : undefined,
    wifiStaticIp: readNullableString(protocolMeta.wifiStaticIp ?? meta.wifiStaticIp) ?? undefined,
    wifiGateway: readNullableString(protocolMeta.wifiGateway ?? meta.wifiGateway) ?? undefined,
    wifiNetmask: readNullableString(protocolMeta.wifiNetmask ?? meta.wifiNetmask) ?? undefined,
    wifiDns: readNullableString(protocolMeta.wifiDns ?? meta.wifiDns) ?? undefined,
    cellularApn: readNullableString(protocolMeta.cellularApn ?? meta.cellularApn) ?? undefined,
    modbusHost: readNullableString(protocolMeta.modbusHost ?? meta.modbusHost) ?? undefined,
    modbusPort: readNullableString(protocolMeta.modbusPort ?? meta.modbusPort) ?? undefined,
    modbusUnitId: readNullableString(protocolMeta.modbusUnitId ?? meta.modbusUnitId) ?? undefined,
    loraAppEui: readNullableString(protocolMeta.loraAppEui ?? meta.loraAppEui) ?? undefined,
    loraDevEui: readNullableString(protocolMeta.loraDevEui ?? meta.loraDevEui) ?? undefined,
  };
}

function mapDeviceToSnapshot(device: DeviceDto): DataloggerFormSnapshot {
  const meta = asJsonObject(device.metadata);
  const basicMeta = asJsonObject(meta.basic);
  const hardwareMeta = asJsonObject(meta.hardware);
  const alertsMeta = asJsonObject(meta.alerts);
  const alertRecipientsMeta = asJsonObject(alertsMeta.recipients);
  const metadataChannels = Array.isArray(meta.channels) ? meta.channels.map(asJsonObject) : [];
  const liveChannels = Array.isArray(device.channels) ? device.channels : [];

  const mergedChannels: ChannelRow[] =
    liveChannels.length > 0
      ? liveChannels.map((channel) => {
          const metadataChannel =
            metadataChannels.find((item) => readString(item.id) === channel.id) ??
            metadataChannels.find((item) => readString(item.key) === channel.key);
          const constDataType = mapChannelDataType(channel.data_type, metadataChannel?.dataType);
          return {
            id: channel.id,
            name: readString(metadataChannel?.name, channel.label),
            channelKey: channel.key,
            unit: readString(metadataChannel?.unit, channel.unit ?? ""),
            dataType: constDataType,
            minValue: readNumber(metadataChannel?.minValue ?? channel.min_value),
            maxValue: readNumber(metadataChannel?.maxValue ?? channel.max_value),
            thresholdWarningLow: readNumber(metadataChannel?.thresholdWarningLow),
            thresholdWarningHigh: readNumber(metadataChannel?.thresholdWarningHigh),
            boolExpected:
              metadataChannel?.boolExpected === "true" || metadataChannel?.boolExpected === "false" || metadataChannel?.boolExpected === "any"
                ? metadataChannel.boolExpected
                : "any",
            stringMaxLength: readNumber(metadataChannel?.stringMaxLength),
            aggregations: sanitizeAggregationsForDataType(
              constDataType,
              mapChannelAggregations(metadataChannel?.aggregations ?? channel.aggregation),
            ),
            prepend: readString(metadataChannel?.prepend),
            append: readString(metadataChannel?.append),
            floatPrecision:
              metadataChannel?.floatPrecision === 1 || metadataChannel?.floatPrecision === 2 || metadataChannel?.floatPrecision === 3
                ? metadataChannel.floatPrecision
                : 2,
            formula: asJsonObject(metadataChannel?.formula).sourceChannelKey
              ? {
                  sourceChannelKey: readString(asJsonObject(metadataChannel?.formula).sourceChannelKey),
                  operator: readChannelFormulaOperator(asJsonObject(metadataChannel?.formula).operator),
                  operand: readNumber(asJsonObject(metadataChannel?.formula).operand) ?? 0,
                }
              : null,
            graphType: sanitizeGraphTypeForDataType(
              constDataType,
              metadataChannel?.graphType === "line" ||
                metadataChannel?.graphType === "bar" ||
                metadataChannel?.graphType === "area" ||
                metadataChannel?.graphType === "scatter" ||
                metadataChannel?.graphType === "gauge" ||
                metadataChannel?.graphType === "step" ||
                metadataChannel?.graphType === "histogram" ||
                metadataChannel?.graphType === "stacked_bar" ||
                metadataChannel?.graphType === "donut" ||
                metadataChannel?.graphType === "heatmap" ||
                metadataChannel?.graphType === "radar" ||
                metadataChannel?.graphType === "candlestick" ||
                metadataChannel?.graphType === "ohlc" ||
                metadataChannel?.graphType === "gantt" ||
                metadataChannel?.graphType === "table"
                ? metadataChannel.graphType
                : defaultGraphTypeForDataType(constDataType),
            ),
          };
        })
      : metadataChannels.length > 0
        ? metadataChannels.map((channel, index) => {
            const constDataType = mapChannelDataType("number", channel.dataType);
            return {
              ...createDefaultChannel(index, metadataChannels.slice(0, index).map((item) => readString(item.key))),
              id: readString(channel.id) || createDefaultChannel(index).id,
              name: readString(channel.name, `Channel ${index + 1}`),
              channelKey: readString(channel.key, `channel_${index + 1}`),
              unit: readString(channel.unit),
              dataType: constDataType,
              minValue: readNumber(channel.minValue),
              maxValue: readNumber(channel.maxValue),
              thresholdWarningLow: readNumber(channel.thresholdWarningLow),
              thresholdWarningHigh: readNumber(channel.thresholdWarningHigh),
              boolExpected:
                channel.boolExpected === "true" || channel.boolExpected === "false" || channel.boolExpected === "any"
                  ? channel.boolExpected
                  : "any",
              stringMaxLength: readNumber(channel.stringMaxLength),
              aggregations: sanitizeAggregationsForDataType(constDataType, mapChannelAggregations(channel.aggregations)),
              prepend: readString(channel.prepend),
              append: readString(channel.append),
              floatPrecision: channel.floatPrecision === 1 || channel.floatPrecision === 2 || channel.floatPrecision === 3 ? channel.floatPrecision : 2,
              formula: asJsonObject(channel.formula).sourceChannelKey
                ? {
                    sourceChannelKey: readString(asJsonObject(channel.formula).sourceChannelKey),
                    operator: readChannelFormulaOperator(asJsonObject(channel.formula).operator),
                    operand: readNumber(asJsonObject(channel.formula).operand) ?? 0,
                  }
                : null,
              graphType: sanitizeGraphTypeForDataType(
                constDataType,
                channel.graphType === "line" ||
                  channel.graphType === "bar" ||
                  channel.graphType === "area" ||
                  channel.graphType === "scatter" ||
                  channel.graphType === "gauge" ||
                  channel.graphType === "step" ||
                  channel.graphType === "histogram" ||
                  channel.graphType === "stacked_bar" ||
                  channel.graphType === "donut" ||
                  channel.graphType === "heatmap" ||
                  channel.graphType === "radar" ||
                  channel.graphType === "candlestick" ||
                  channel.graphType === "ohlc" ||
                  channel.graphType === "gantt" ||
                  channel.graphType === "table"
                  ? channel.graphType
                  : defaultGraphTypeForDataType(constDataType),
              ),
            };
        })
        : [createDefaultChannel(0)];

  const rawConditions = Array.isArray(alertsMeta.conditions) ? alertsMeta.conditions.map(asJsonObject) : [];
  const alerts: DataloggerAlertCondition[] = rawConditions.map((condition, index) => ({
    enabled: readBoolean(condition.enabled, true),
    channelId: readNullableString(condition.channelId),
    channelKey: readString(condition.channelKey, mergedChannels[0]?.channelKey ?? ""),
    operator:
      condition.operator === ">" ||
      condition.operator === "<" ||
      condition.operator === ">=" ||
      condition.operator === "<=" ||
      condition.operator === "=" ||
      condition.operator === "==" ||
      condition.operator === "!="
        ? condition.operator
        : index % 2 === 0
          ? ">"
          : "<",
    threshold: readString(condition.threshold, "0"),
    source:
      condition.source === "warning_low" || condition.source === "warning_high" || condition.source === "manual"
        ? condition.source
        : "manual",
  }));

  const lifecycle: DataloggerLifecycle = {
    installationDate: readString(meta.installation_date),
    warrantyEnd: readString(meta.warranty_end),
    nextCalibration: readString(meta.next_calibration),
  };
  const visualizationsMeta = asJsonObject(meta.visualizations);
  const windRoseMeta = asJsonObject(visualizationsMeta.wind_rose);
  const visualizations: DataloggerVisualizations = {
    windRose: {
      enabled: readBoolean(windRoseMeta.enabled),
      speedChannelKey: readString(windRoseMeta.speed_channel_key),
      directionChannelKey: readString(windRoseMeta.direction_channel_key),
    },
  };

  const samplingRateValue = readNumber(hardwareMeta.samplingRateValue) ?? 1;
  const samplingRatePeriod = readSamplingPeriod(hardwareMeta.samplingRatePeriod);
  const transmissionMode = readTransmissionMode(
    hardwareMeta.transmissionMode ?? meta.transmission_mode ?? meta.protocol,
  );

  const alertRecipients: DataloggerAlertRecipients = {
    admin: {
      email: readBoolean(asJsonObject(alertRecipientsMeta.admin).email, readBoolean(alertRecipientsMeta.notifyAdmin, true) && readBoolean(alertRecipientsMeta.enableEmail, true)),
      message: readBoolean(asJsonObject(alertRecipientsMeta.admin).message, readBoolean(alertRecipientsMeta.notifyAdmin, true) && readBoolean(alertRecipientsMeta.enableMessage, false)),
      inApp: readBoolean(asJsonObject(alertRecipientsMeta.admin).inApp, readBoolean(alertRecipientsMeta.notifyAdmin, true)),
      selectAll: readBoolean(asJsonObject(alertRecipientsMeta.admin).selectAll),
      userIds: readAlertAudienceUserIds(asJsonObject(alertRecipientsMeta.admin).userIds),
    },
    distributor: {
      email: readBoolean(asJsonObject(alertRecipientsMeta.distributor).email, readBoolean(alertRecipientsMeta.notifyDistributor, false) && readBoolean(alertRecipientsMeta.enableEmail, true)),
      message: readBoolean(asJsonObject(alertRecipientsMeta.distributor).message, readBoolean(alertRecipientsMeta.notifyDistributor, false) && readBoolean(alertRecipientsMeta.enableMessage, false)),
      inApp: readBoolean(asJsonObject(alertRecipientsMeta.distributor).inApp, readBoolean(alertRecipientsMeta.notifyDistributor, false)),
      selectAll: readBoolean(asJsonObject(alertRecipientsMeta.distributor).selectAll),
      userIds: readAlertAudienceUserIds(asJsonObject(alertRecipientsMeta.distributor).userIds),
    },
    users: {
      email: readBoolean(asJsonObject(alertRecipientsMeta.users).email, readBoolean(alertRecipientsMeta.notifyUsers, false) && readBoolean(alertRecipientsMeta.enableEmail, true)),
      message: readBoolean(asJsonObject(alertRecipientsMeta.users).message, readBoolean(alertRecipientsMeta.notifyUsers, false) && readBoolean(alertRecipientsMeta.enableMessage, false)),
      inApp: readBoolean(asJsonObject(alertRecipientsMeta.users).inApp, readBoolean(alertRecipientsMeta.notifyUsers, false)),
      selectAll: readBoolean(asJsonObject(alertRecipientsMeta.users).selectAll),
      userIds: readAlertAudienceUserIds(asJsonObject(alertRecipientsMeta.users).userIds),
    },
    emailRecipients: readStringArray(alertRecipientsMeta.emailRecipients).join(", "),
    messageRecipients: readStringArray(alertRecipientsMeta.messageRecipients).join(", "),
  };

  const stateId = readString(basicMeta.state_id, readString(meta.state_id));

  return {
    basic: {
      name: readString(basicMeta.name, readString(device.name)),
      deviceCategoryId: device.device_category_id ?? "",
      description: readString(basicMeta.description, readString(meta.description)),
      simType:
        readString(basicMeta.sim_type, readString(meta.sim_type)).toLowerCase() === "industrial"
          ? "industrial"
          : readString(basicMeta.sim_type, readString(meta.sim_type)).toLowerCase() === "other"
            ? "other"
            : "phone",
      phoneNumber: readString(basicMeta.phone_number, readString(meta.phone_number)),
      imeiNumber: readString(basicMeta.imei_number, readString(meta.imei_number)),
      location: readString(basicMeta.location, readString(meta.location, readString(meta.site))),
      city: readString(basicMeta.city, readString(meta.city)),
      stateId,
      state: stateId ? readString(basicMeta.state, readString(meta.state)) : "",
      tags: readStringArray(basicMeta.tags ?? meta.tags),
      latitude:
        readNumber(meta.latitude)?.toString() ??
        readNullableString(meta.latitude) ??
        readNumber(meta.lat)?.toString() ??
        readNullableString(meta.lat) ??
        "",
      longitude:
        readNumber(meta.longitude)?.toString() ??
        readNullableString(meta.longitude) ??
        readNumber(meta.lng)?.toString() ??
        readNullableString(meta.lng) ??
        "",
      deviceImagePath: readNullableString(meta.device_photo_path),
      deviceImagePreviewUrl: null,
    },
    hardware: {
      channelCount: mergedChannels.length,
      samplingRateValue,
      samplingRatePeriod,
      transmissionMode,
      protocolSettings: extractProtocolSettings(meta, hardwareMeta),
      samplingRate: readNumber(hardwareMeta.samplingRateHz) ?? samplingRateValue,
    },
    channels: mergedChannels,
    lifecycle,
    alerts,
    alertRecipients,
    visualizations,
    qrPublic: {
      enabled: device.qr_public_enabled ?? true,
      displayFields: (device.qr_display_fields ?? ["image", "name", "location", "status", "last_seen"]).filter(
        (field): field is "image" | "name" | "location" | "status" | "last_seen" | "installation_date" | "warranty_end" | "channels" =>
          ["image", "name", "location", "status", "last_seen", "installation_date", "warranty_end", "channels"].includes(field),
      ),
    },
  };
}

function serializeSnapshot(snapshot: DataloggerFormSnapshot): string {
  return JSON.stringify({
    basic: {
      ...snapshot.basic,
      deviceImagePreviewUrl: null,
    },
    hardware: snapshot.hardware,
    channels: snapshot.channels,
    lifecycle: snapshot.lifecycle,
    alerts: snapshot.alerts,
    alertRecipients: snapshot.alertRecipients,
    visualizations: snapshot.visualizations,
    qrPublic: snapshot.qrPublic,
  });
}

function toTopLevelProtocolMetadata(protocolSettings: HardwareProtocolSettings): JsonObject {
  const next: JsonObject = {};
  for (const [key, value] of Object.entries(protocolSettings)) {
    if (key === "mqttPassword") {
      continue;
    }
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === "string" && value.trim() === "") {
      continue;
    }
    next[key] = value as JsonValue;
  }
  if (protocolSettings.mqttBroker) {
    next.mqtt_broker = protocolSettings.mqttBroker;
  }
  if (protocolSettings.mqttPort) {
    const mqttPort = Number(protocolSettings.mqttPort);
    next.mqtt_port = Number.isFinite(mqttPort) ? mqttPort : protocolSettings.mqttPort;
  }
  return next;
}

function buildMergedMetadata(existing: JsonObject, snapshot: DataloggerFormSnapshot): JsonObject {
  const next: JsonObject = { ...existing };
  for (const key of CONTROLLED_METADATA_KEYS) {
    delete next[key];
  }

  const snapshotMeta = snapshotToMetadata(snapshot);
  const legacyProtocolMeta = toTopLevelProtocolMetadata(snapshot.hardware.protocolSettings);

  Object.assign(next, snapshotMeta, legacyProtocolMeta, {
    location: snapshot.basic.location.trim() || null,
    city: snapshot.basic.city.trim() || null,
    state_id: snapshot.basic.stateId.trim() || null,
    state: snapshot.basic.state.trim() || null,
    site: snapshot.basic.location.trim() || null,
    description: snapshot.basic.description.trim() || null,
    sim_type: snapshot.basic.simType,
    phone_number: snapshot.basic.phoneNumber.trim() || null,
    imei_number: snapshot.basic.imeiNumber.trim() || null,
    tags: snapshot.basic.tags,
    transmission_mode: snapshot.hardware.transmissionMode,
    protocol: snapshot.hardware.transmissionMode,
  });

  if (snapshot.basic.latitude.trim() && snapshot.basic.longitude.trim()) {
    next.lat = snapshot.basic.latitude.trim();
    next.lng = snapshot.basic.longitude.trim();
  }

  if (!snapshot.basic.deviceImagePath) {
    delete next.device_photo_path;
  }

  if (!snapshot.basic.location.trim()) {
    delete next.location;
  }
  if (!snapshot.basic.city.trim()) {
    delete next.city;
  }
  if (!snapshot.basic.state.trim()) {
    delete next.state;
  }
  if (!snapshot.basic.stateId.trim()) {
    delete next.state_id;
  }
  if (!snapshot.basic.description.trim()) {
    delete next.description;
  }
  if (!snapshot.basic.phoneNumber.trim()) {
    delete next.phone_number;
  }
  if (!snapshot.basic.imeiNumber.trim()) {
    delete next.imei_number;
  }

  return next;
}

function filterSupportedAggregations(aggregations: ChannelAggregation[]): Array<"avg" | "min" | "max"> | null {
  const allowed = aggregations.filter(
    (aggregation): aggregation is "avg" | "min" | "max" => ALLOWED_CHANNEL_AGGREGATIONS.has(aggregation),
  );
  return allowed.length > 0 ? allowed : null;
}

function mapChannelToApiBody(channel: ChannelRow): DeviceChannelCreateDto {
  return {
    key: channel.channelKey.trim(),
    label: channel.name.trim(),
    unit: channel.unit.trim() || null,
    data_type: channel.dataType === "bool" ? "boolean" : channel.dataType === "string" ? "string" : "number",
    min_value: channel.minValue,
    max_value: channel.maxValue,
    aggregation: filterSupportedAggregations(channel.aggregations),
  };
}

function mapChannelToUpdateBody(channel: ChannelRow): Partial<DeviceChannelCreateDto> {
  const body: Partial<DeviceChannelCreateDto> = mapChannelToApiBody(channel);
  delete body.key;
  return body;
}

function tabForError(error: string): DeviceDetailTabId {
  if (error.includes("public QR field")) {
    return "qr";
  }
  const fieldKey = invalidFieldKeyForError(error);
  if (fieldKey?.startsWith("basic.")) {
    return "overview";
  }
  if (fieldKey?.startsWith("channel.")) {
    return "channels";
  }
  if (fieldKey?.startsWith("visualizations.")) {
    return "visualizations";
  }
  if (fieldKey?.startsWith("hardware.")) {
    return "data";
  }
  if (fieldKey?.startsWith("alerts.")) {
    return "alerts";
  }
  if (fieldKey?.startsWith("protocol.")) {
    return "connectivity";
  }
  return "overview";
}

function validateEditableSnapshot(snapshot: DataloggerFormSnapshot): string[] {
  const errors = [
    ...validateBasic(snapshot.basic),
    ...validateChannels(snapshot.channels),
    ...validateHardware(snapshot.hardware),
    ...validateAlertTemplates(snapshot.alerts, snapshot.channels),
    ...validateVisualizations(snapshot.visualizations, snapshot.channels),
  ];
  if (snapshot.qrPublic.enabled && snapshot.qrPublic.displayFields.length === 0) {
    errors.push("Select at least one public QR field or disable the public QR page.");
  }
  return errors;
}

export type DeviceDetailShellProps = {
  device: DeviceDto;
  initialEdit?: boolean;
  initialTab?: string | null;
};

export function DeviceDetailShell({ device, initialTab }: DeviceDetailShellProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canEditDevices = hasModulePermission(user, permissionMap, "devices", "edit");
  const [tab, setTabState] = useState<DeviceDetailTabId>(() => resolveTab(initialTab ?? null));
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [stepErrors, setStepErrors] = useState<string[]>([]);
  const [invalidFieldKey, setInvalidFieldKey] = useState<string | null>(null);
  const [initialSnapshotSerialized, setInitialSnapshotSerialized] = useState("");

  const opModel = deviceActivityModelFromApi(device);
  const headerVariant = operationalStatusToIndicatorVariant(opModel.status);

  const patchDevice = usePatchDeviceMutation();
  const createChannel = useCreateDeviceChannelMutation(device.id);
  const updateChannel = useUpdateDeviceChannelMutation(device.id);
  const deleteChannel = useDeleteDeviceChannelMutation(device.id);

  const basicState = useDataloggerWizardStore((s) => s.basic);
  const channelsState = useDataloggerWizardStore((s) => s.channels);
  const hardwareDraftState = useDataloggerWizardStore((s) => s.hardwareDraft);
  const lifecycleState = useDataloggerWizardStore((s) => s.lifecycle);
  const alertConditionsState = useDataloggerWizardStore((s) => s.alertConditions);
  const alertRecipientsState = useDataloggerWizardStore((s) => s.alertRecipients);
  const visualizationsState = useDataloggerWizardStore((s) => s.visualizations);
  const qrPublicState = useDataloggerWizardStore((s) => s.qrPublic);
  const getSnapshot = useDataloggerWizardStore((s) => s.getSnapshot);
  const loadSnapshot = useDataloggerWizardStore((s) => s.loadSnapshot);
  const setLifecycle = useDataloggerWizardStore((s) => s.setLifecycle);

  const currentSnapshotSerialized = useMemo(
    () =>
      serializeSnapshot({
        basic: basicState,
        channels: channelsState,
        lifecycle: lifecycleState,
        alerts: alertConditionsState,
        alertRecipients: alertRecipientsState,
        visualizations: visualizationsState,
        qrPublic: qrPublicState,
        hardware: {
          channelCount: channelsState.length,
          samplingRateValue: hardwareDraftState.samplingRateValue,
          samplingRatePeriod: hardwareDraftState.samplingRatePeriod,
          transmissionMode: hardwareDraftState.transmissionMode,
          protocolSettings: hardwareDraftState.protocolSettings,
          samplingRate: samplesPerPeriodToHz(
            hardwareDraftState.samplingRateValue,
            hardwareDraftState.samplingRatePeriod,
          ),
        },
      }),
    [alertConditionsState, alertRecipientsState, basicState, channelsState, hardwareDraftState, lifecycleState, qrPublicState, visualizationsState],
  );
  const isDirty = currentSnapshotSerialized !== initialSnapshotSerialized;
  const enabledAlertsCount = alertConditionsState.filter((alert) => alert.enabled).length;
  const tabSummary = useMemo(
    () => ({
      overview: basicState.location || "Identity and location",
      channels: `${channelsState.length} channel${channelsState.length === 1 ? "" : "s"}`,
      visualizations: visualizationsState.windRose.enabled ? "Wind rose enabled" : "Optional",
      data: samplingPeriodMeta(hardwareDraftState.samplingRatePeriod),
      alerts: `${enabledAlertsCount} condition${enabledAlertsCount === 1 ? "" : "s"}`,
      connectivity: hardwareDraftState.transmissionMode,
      lifecycle: lifecycleState.installationDate || lifecycleState.nextCalibration || "Dates optional",
      qr: qrPublicState.enabled ? `${qrPublicState.displayFields.length} public fields` : "Disabled",
      debug: "Logs and activity",
    }),
    [
      basicState.location,
      channelsState.length,
      enabledAlertsCount,
      hardwareDraftState.samplingRatePeriod,
      hardwareDraftState.transmissionMode,
      lifecycleState.installationDate,
      lifecycleState.nextCalibration,
      qrPublicState.displayFields.length,
      qrPublicState.enabled,
      visualizationsState.windRose.enabled,
    ],
  );
  const tabItems = useMemo(
    () =>
      TABS.map((item, index) => ({
        id: item.id,
        label: item.label,
        summary: tabSummary[item.id],
        icon: TAB_ICONS[index] ?? CheckCircle2,
      })),
    [tabSummary],
  );
  const activeTabIndex = Math.max(0, tabItems.findIndex((item) => item.id === tab));

  useEffect(() => {
    setTabState(resolveTab(initialTab ?? null));
  }, [device.id, initialTab]);

  useEffect(() => {
    const onPop = () => {
      const qs = new URLSearchParams(window.location.search);
      setTabState(resolveTab(qs.get("tab")));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const nextSnapshot = mapDeviceToSnapshot(device);
    loadSnapshot(nextSnapshot, 0);
    setInitialSnapshotSerialized(serializeSnapshot(nextSnapshot));
    setStepErrors([]);
    setInvalidFieldKey(null);
    setSaveError(null);
    setSaveMessage(null);
  }, [device.id, loadSnapshot]);

  useEffect(() => {
    if (!invalidFieldKey) {
      return;
    }
    const id = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[${INVALID_FIELD_ATTR}="${invalidFieldKey}"]`);
      if (!el) {
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        el.focus({ preventScroll: true });
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [invalidFieldKey, tab]);

  const setTab = useCallback(
    (id: DeviceDetailTabId) => {
      setTabState(id);
      const next = new URLSearchParams(window.location.search);
      next.set("tab", id);
      navigate(`/devices/${device.id}/edit?${next.toString()}`, { replace: true });
    },
    [device.id, navigate],
  );

  const clearValidationState = useCallback(() => {
    if (stepErrors.length > 0 || invalidFieldKey || saveError || saveMessage) {
      setStepErrors([]);
      setInvalidFieldKey(null);
      setSaveError(null);
      setSaveMessage(null);
    }
  }, [invalidFieldKey, saveError, saveMessage, stepErrors.length]);

  const onReset = useCallback(() => {
    const nextSnapshot = mapDeviceToSnapshot(device);
    loadSnapshot(nextSnapshot, 0);
    setInitialSnapshotSerialized(serializeSnapshot(nextSnapshot));
    setStepErrors([]);
    setInvalidFieldKey(null);
    setSaveError(null);
    setSaveMessage(null);
  }, [device, loadSnapshot]);

  const onSave = useCallback(async () => {
    const nextSnapshot = getSnapshot();
    const errors = validateEditableSnapshot(nextSnapshot);
    if (errors.length > 0) {
      setStepErrors(errors);
      setInvalidFieldKey(invalidFieldKeyForError(errors[0]!) ?? null);
      setSaveMessage(null);
      setSaveError(errors[0]!);
      setTab(tabForError(errors[0]!));
      return;
    }

    try {
      setSaveError(null);
      setSaveMessage(null);

      const nextMetadata = buildMergedMetadata(asJsonObject(device.metadata), nextSnapshot);
      const mqttConnection = buildMqttConnectionPayload(nextSnapshot, {
        serialNumber: "",
        apiKey: "",
        deviceUid: device.device_uid,
      });
      const websocketConnection = buildWebsocketConnectionPayload(nextSnapshot);
      const bluetoothConnection = buildBluetoothSerialConnectionPayload(nextSnapshot);
      await patchDevice.mutateAsync({
        deviceId: device.id,
        body: {
          name: nextSnapshot.basic.name.trim() || null,
          device_category_id: nextSnapshot.basic.deviceCategoryId || null,
          qr_public_enabled: nextSnapshot.qrPublic.enabled,
          qr_display_fields: nextSnapshot.qrPublic.displayFields,
          metadata: nextMetadata,
          mqtt_connection: mqttConnection,
          websocket_connection: websocketConnection,
          bluetooth_connection: bluetoothConnection,
        },
      });

      const existingChannels = Array.isArray(device.channels) ? device.channels : [];
      const existingIds = new Set(existingChannels.map((channel) => channel.id));
      const nextIds = new Set(
        nextSnapshot.channels.map((channel) => channel.id).filter((channelId) => existingIds.has(channelId)),
      );

      for (const channel of nextSnapshot.channels) {
        if (existingIds.has(channel.id)) {
          await updateChannel.mutateAsync({
            channelId: channel.id,
            body: mapChannelToUpdateBody(channel),
          });
        } else {
          await createChannel.mutateAsync(mapChannelToApiBody(channel));
        }
      }

      for (const channel of existingChannels) {
        if (!nextIds.has(channel.id)) {
          await deleteChannel.mutateAsync(channel.id);
        }
      }

      setInitialSnapshotSerialized(serializeSnapshot(nextSnapshot));
      setStepErrors([]);
      setInvalidFieldKey(null);
      setSaveMessage("Changes saved.");
    } catch (error) {
      setSaveMessage(null);
      setSaveError(error instanceof Error ? error.message : "Could not save device changes.");
    }
  }, [createChannel, deleteChannel, device.channels, device.id, device.metadata, getSnapshot, patchDevice, setTab, updateChannel]);

  const persistUploadedPhoto = useCallback(
    async (path: string) => {
      const snapshot = getSnapshot();
      const withPhoto: DataloggerFormSnapshot = {
        ...snapshot,
        basic: { ...snapshot.basic, deviceImagePath: path },
      };
      const nextMetadata = buildMergedMetadata(asJsonObject(device.metadata), withPhoto);
      await patchDevice.mutateAsync({
        deviceId: device.id,
        body: { metadata: nextMetadata },
      });
      setInitialSnapshotSerialized(serializeSnapshot(withPhoto));
      setSaveError(null);
      setSaveMessage("Photo saved.");
    },
    [device.id, device.metadata, getSnapshot, patchDevice],
  );

  const savePending =
    patchDevice.isPending || createChannel.isPending || updateChannel.isPending || deleteChannel.isPending;
  const canManageServiceRecords =
    user?.role === "super_admin" || (user?.accountType === "platform" && canEditDevices);

  return (
    <div className="space-y-ds-6 pb-28">
      <PageHeader
        title={device.name?.trim() || device.device_uid}
        titleAdornment={<span className={cn("inline-flex shrink-0 items-center gap-ds-2 rounded-full border px-ds-2 py-0.5 text-micro font-bold uppercase tracking-wide", opModel.status === DeviceOperationalStatus.VISUALIZATION_DISABLED && "border-warning/35 bg-warning/12 text-warning", opModel.status === DeviceOperationalStatus.INGESTION_DISABLED && "border-error/35 bg-error/12 text-error", opModel.status === DeviceOperationalStatus.ONLINE && "border-success/35 bg-success/10 text-success", opModel.status === DeviceOperationalStatus.STALE && "border-warning/35 bg-warning/12 text-warning", opModel.status === DeviceOperationalStatus.OFFLINE && "border-border/70 bg-muted-bg/55 text-muted", opModel.status === DeviceOperationalStatus.ERROR && "border-error/35 bg-error/12 text-error", opModel.status === DeviceOperationalStatus.MAINTENANCE && "border-accent/35 bg-accent/12 text-accent")}><StatusIndicator variant={headerVariant} size="sm" pulse={opModel.status === DeviceOperationalStatus.ONLINE} label={`Operational: ${formatDeviceOperationalStatusLabel(opModel.status)}`} />{formatDeviceOperationalStatusLabel(opModel.status)}</span>}
      />

      <StepTabs
        ariaLabel="Device sections"
        items={tabItems}
        activeId={tab}
        onSelect={(id) => setTab(id as DeviceDetailTabId)}
        hideProgressHeader
        compact
      />

      {saveError && stepErrors.length === 0 ? (
        <p className="rounded-[var(--radius-lg)] border border-error/25 bg-error/10 px-ds-4 py-ds-3 text-caption text-error" role="alert">
          {saveError}
        </p>
      ) : null}

      {tab === "overview" ? (
        <StepBasic
          includeConnectivity={false}
          invalidFieldKey={invalidFieldKey}
          validationErrors={stepErrors}
          onFieldEdit={clearValidationState}
          onPhotoUploaded={persistUploadedPhoto}
        />
      ) : null}

      {tab === "channels" ? (
        <StepChannels
          invalidFieldKey={invalidFieldKey}
          validationErrors={stepErrors}
          onFieldEdit={clearValidationState}
          lockedChannelIds={new Set((device.channels ?? []).map((channel) => channel.id))}
        />
      ) : null}

      {tab === "visualizations" ? (
        <VisualizationsTab invalidFieldKey={invalidFieldKey} onFieldEdit={clearValidationState} />
      ) : null}

      {tab === "data" ? (
        <DataSamplingTab invalidFieldKey={invalidFieldKey} onFieldEdit={clearValidationState} />
      ) : null}

      {tab === "alerts" ? (
        <AlertsTab
          invalidFieldKey={invalidFieldKey}
          validationErrors={stepErrors}
          onFieldEdit={clearValidationState}
        />
      ) : null}

      {tab === "connectivity" ? (
        <StepBasicConnectivity
          invalidFieldKey={invalidFieldKey}
          validationErrors={stepErrors}
          onFieldEdit={clearValidationState}
        />
      ) : null}

      {tab === "lifecycle" ? (
        <LifecycleTab onFieldEdit={clearValidationState} />
      ) : null}

      {tab === "qr" ? <QrPublicTab validationError={stepErrors.find((error) => error.includes("public QR field")) ?? null} /> : null}

      {tab === "debug" ? (
        <div className="space-y-ds-5">
          <DeviceServiceRecords
            deviceId={device.id}
            canManage={canManageServiceRecords}
            onCalibrationRecorded={(nextCalibrationDate) => setLifecycle({ nextCalibration: nextCalibrationDate })}
          />
          <DeviceDebugAudit deviceId={device.id} />
          <Card className="border-border/60 bg-card p-ds-4 shadow-sm">
            <p className="text-caption text-muted">
              Open Live for streaming inspection; command history appears below.
            </p>
          </Card>
        
        </div>
      ) : null}

      <div className="pointer-events-none fixed inset-x-0 bottom-5 z-[1100] flex justify-center px-ds-3 md:left-[var(--sidebar-width-collapsed)]">
        <div className="pointer-events-auto inline-flex max-w-full flex-wrap items-center justify-center gap-ds-2 rounded-control border border-primary/20 bg-[var(--accent-primary-soft)] px-ds-3 py-ds-2 shadow-overlay backdrop-blur-md">
          <Link to="/devices" className="btn btn-secondary btn-sm !no-underline">Cancel</Link>
          {isDirty ? <span className="hidden text-caption text-warning sm:inline">Unsaved changes</span> : null}
          {!isDirty && saveMessage ? <span className="hidden text-caption text-success sm:inline">{saveMessage}</span> : null}
          <div className="flex items-center gap-ds-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setTab(tabItems[activeTabIndex - 1]!.id as DeviceDetailTabId)} disabled={activeTabIndex === 0}>
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Prev
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setTab(tabItems[activeTabIndex + 1]!.id as DeviceDetailTabId)} disabled={activeTabIndex === tabItems.length - 1}>
              Next
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onReset} disabled={!isDirty || savePending}>
              <Undo2 className="h-4 w-4" aria-hidden />
              Reset
            </Button>
            <Button type="button" size="sm" onClick={() => void onSave()} disabled={!isDirty || savePending}>
              <Save className="h-4 w-4" aria-hidden />
              {savePending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
